import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, Download } from 'lucide-react';

const Analytics = () => {
  const [courses, setCourses] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTest, setSelectedTest] = useState('');
  const [courseAnalytics, setCourseAnalytics] = useState(null);
  const [testAnalytics, setTestAnalytics] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchCourseAnalytics();
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedTest) {
      fetchTestAnalytics();
    }
  }, [selectedTest]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchCourseAnalytics = async () => {
    try {
      const response = await axios.get(`/admin/analytics/course/${selectedCourse}`);
      setCourseAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch course analytics:', error);
    }
  };

  const fetchTestAnalytics = async () => {
    try {
      const response = await axios.get(`/admin/analytics/test/${selectedTest}`);
      setTestAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch test analytics:', error);
    }
  };

  const exportToCSV = (data, filename) => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    for (const row of data) {
      const values = headers.map(header => {
        const val = row[header];
        return `"${val}"`;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">Analytics & Reports</h2>
        <p className="text-sm sm:text-base text-gray-600 mt-1">View performance metrics and export reports</p>
      </div>

      {/* Course Analytics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Course Analytics
          </CardTitle>
          <CardDescription>View enrollment and activity statistics for courses</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Select Course</label>
            <Select value={selectedCourse || undefined} onValueChange={setSelectedCourse}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a course" />
              </SelectTrigger>
              <SelectContent>
                {courses.map((course) => (
                  <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {courseAnalytics && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <Card>
                <CardContent className="pt-4 sm:pt-6">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">{courseAnalytics.total_enrollments}</p>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Total Enrollments</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 sm:pt-6">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{courseAnalytics.active_enrollments}</p>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Active Enrollments</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 sm:pt-6">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-bold text-blue-700">{courseAnalytics.total_test_attempts}</p>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Test Attempts</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {courseAnalytics && courseAnalytics.enrollments.length > 0 && (
            <div className="flex justify-end">
              <Button 
                onClick={() => exportToCSV(courseAnalytics.enrollments, `course_${selectedCourse}_enrollments.csv`)}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Enrollments
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Analytics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Test Analytics
          </CardTitle>
          <CardDescription>View performance statistics for specific tests</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Enter Test ID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={selectedTest}
                onChange={(e) => setSelectedTest(e.target.value)}
                placeholder="Enter test ID"
                className="flex-1 px-3 py-2 border rounded-md"
              />
              <Button onClick={fetchTestAnalytics}>Analyze</Button>
            </div>
          </div>

          {testAnalytics && testAnalytics.total_attempts > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <Card>
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-2xl font-bold text-blue-600">{testAnalytics.total_attempts}</p>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">Total Attempts</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-2xl font-bold text-blue-600">{testAnalytics.average_score}</p>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">Average Score</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-2xl font-bold text-green-600">{testAnalytics.pass_rate}%</p>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">Pass Rate</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-2xl font-bold text-orange-600">{testAnalytics.passing_marks}</p>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">Passing Marks</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-sm sm:text-base">Recent Attempts</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {testAnalytics.attempts.slice(0, 10).map((attempt, index) => (
                      <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-2 sm:p-3 border rounded-lg gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-medium truncate">User ID: {attempt.user_id.substring(0, 8)}...</p>
                          <p className="text-xs text-gray-500">{new Date(attempt.completed_at).toLocaleString()}</p>
                        </div>
                        <div className="text-left sm:text-right flex-shrink-0">
                          <p className="font-semibold text-blue-600 text-sm">{attempt.score.toFixed(2)} / {attempt.total_questions}</p>
                          <p className="text-xs text-gray-500">{((attempt.score / attempt.total_questions) * 100).toFixed(1)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button 
                  onClick={() => exportToCSV(testAnalytics.attempts, `test_${selectedTest}_attempts.csv`)}
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Test Results
                </Button>
              </div>
            </>
          )}

          {testAnalytics && testAnalytics.total_attempts === 0 && (
            <div className="text-center py-12 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No attempts found for this test</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Export Options</CardTitle>
          <CardDescription>Export various reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <Button
              variant="outline"
              className="text-xs sm:text-sm"
              onClick={async () => {
                const response = await axios.get('/admin/users');
                exportToCSV(response.data, 'all_users.csv');
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export All Users
            </Button>
            <Button
              variant="outline"
              className="text-xs sm:text-sm"
              onClick={async () => {
                const response = await axios.get('/admin/enrollments');
                exportToCSV(response.data, 'all_enrollments.csv');
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export All Enrollments
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
